import type { IntegrationKey } from "schemas/agentTools/integrationDefinitions";
import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import upperFirst from "lodash-es/upperFirst";
import { filterNulls } from "replo-utils/lib/array";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { SANDBOX_DEV_PATH } from "replo-utils/lib/sandboxPaths";
import { z } from "zod";

export type ToolDisplayText =
  | string
  | {
      completed: string;
      error?: string;
      pending?: string;
      running: string;
    };

export type OpencodeToolDisplay = {
  header?: ToolDisplayText | null;
  integrationKey?: IntegrationKey;
  summary: ToolDisplayText | null;
  detail: string | null;
};

export type OpencodeToolDisplayContext = {
  currentSessionToolParts?: readonly OpencodeToolPart[];
};

export function getToolDisplayText({
  completed,
  error,
  pending,
  running,
  target,
}: {
  completed: string;
  error?: string;
  pending?: string;
  running: string;
  target?: string | null;
}) {
  if (!target) {
    return {
      completed,
      error,
      pending: pending ?? running,
      running,
    };
  }

  return {
    completed: `${completed} ${target}`,
    error: error ? `${error} ${target}` : undefined,
    pending: `${pending ?? running} ${target}`,
    running: `${running} ${target}`,
  };
}

export function getToolDisplayTextValue({
  text,
  status,
}: {
  text: ToolDisplayText | null;
  status: OpencodeToolPart["state"]["status"];
}) {
  if (!text) {
    return null;
  }

  if (typeof text === "string") {
    return text;
  }

  return exhaustiveSwitch({ type: status })<string>({
    completed: () => text.completed,
    error: () => text.error ?? text.completed,
    pending: () => text.pending ?? text.running,
    running: () => text.running,
  });
}

export function appendToolDisplayText({
  text,
  suffix,
}: {
  text: ToolDisplayText;
  suffix: string | null;
}) {
  if (!suffix) {
    return text;
  }

  if (typeof text === "string") {
    return `${text} ${suffix}`;
  }

  return {
    completed: `${text.completed} ${suffix}`,
    error: text.error ? `${text.error} ${suffix}` : undefined,
    pending: `${text.pending ?? text.running} ${suffix}`,
    running: `${text.running} ${suffix}`,
  };
}

const SITE_DIRECTORY_PREFIX_RE = new RegExp(`^${SANDBOX_DEV_PATH}/[^/]+/`);

function getRelativePath({ filePath }: { filePath: string }) {
  return filePath.replace(SITE_DIRECTORY_PREFIX_RE, "");
}

export function getFilePathDisplay({
  prefix,
  toolPart,
}: {
  prefix: ToolDisplayText;
  toolPart: OpencodeToolPart;
}) {
  const filePath = getObjectDetail({
    properties: ["path", "filePath", "file_path"],
    value: toolPart.state.input,
  });

  return {
    summary: !filePath
      ? prefix
      : appendToolDisplayText({
          text: prefix,
          suffix: getRelativePath({ filePath }),
        }),
    detail: null,
  };
}

export function quotedText({ value }: { value: string }) {
  return `"${value}"`;
}

export function quotedDetail({ value }: { value: string | null }) {
  return value ? quotedText({ value }) : null;
}

export function capitalizeFirstLetter({ value }: { value: string }) {
  return upperFirst(value);
}

export function titleCase({ value }: { value: string }) {
  return value.split("_").map(upperFirst).join(" ");
}

function isVowel({ letter }: { letter: string | undefined }) {
  return letter !== undefined && /^[aeiou]$/.test(letter);
}

// Irregular verbs whose past/progressive forms can't be derived by the regular
// rules below. Keyed by lowercase base verb so every conjugation call site
// (bash, integrations, tasks/skills) gets correct tenses from one source.
const IRREGULAR_VERB_TENSE_BY_BASE: Record<
  string,
  { past: string; progressive: string }
> = {
  find: { past: "found", progressive: "finding" },
  get: { past: "got", progressive: "getting" },
  read: { past: "read", progressive: "reading" },
  run: { past: "ran", progressive: "running" },
  take: { past: "took", progressive: "taking" },
  write: { past: "wrote", progressive: "writing" },
};

export function getRegularPastTense({ verb }: { verb: string }) {
  const irregular = IRREGULAR_VERB_TENSE_BY_BASE[verb.toLowerCase()];
  if (irregular) return irregular.past;

  if (verb.endsWith("e")) return `${verb}d`;
  if (
    verb.endsWith("y") &&
    !isVowel({ letter: verb.charAt(verb.length - 2) })
  ) {
    return `${verb.slice(0, -1)}ied`;
  }

  return `${verb}ed`;
}

export function getRegularProgressiveTense({ verb }: { verb: string }) {
  const irregular = IRREGULAR_VERB_TENSE_BY_BASE[verb.toLowerCase()];
  if (irregular) return irregular.progressive;

  if (verb.endsWith("ie")) return `${verb.slice(0, -2)}ying`;
  if (verb.endsWith("e")) return `${verb.slice(0, -1)}ing`;

  return `${verb}ing`;
}

export function normalizeIdentifier({ value }: { value: string }) {
  return value
    .replace(/([\da-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^\d_a-z]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function getQuotedArrayDetail({ value }: { value: unknown }) {
  if (!Array.isArray(value)) {
    return null;
  }

  const details = value.flatMap((item) => {
    const parsedItem = displayPrimitiveSchema.safeParse(item);
    return parsedItem.success ? [quotedText({ value: parsedItem.data })] : [];
  });

  return details.length > 0 ? details.join(", ") : null;
}

export function getQuotedObjectArrayDetail({
  itemProperties,
  value,
}: {
  itemProperties: readonly string[];
  value: unknown;
}) {
  if (!Array.isArray(value)) {
    return null;
  }

  const details = value.flatMap((item) => {
    const detail = getObjectDetail({
      properties: itemProperties,
      value: parseRecordOrNull(item),
    });
    return detail ? [quotedText({ value: detail })] : [];
  });

  return details.length > 0 ? details.join(", ") : null;
}

const recordSchema = z.record(z.string(), z.unknown());

export function parseRecordOrNull(value: unknown) {
  const parsedValue = recordSchema.safeParse(value);
  return parsedValue.success ? parsedValue.data : null;
}

const displayPrimitiveSchema = z
  .union([z.string().trim().min(1), z.number(), z.boolean()])
  .transform((value) => String(value));

function getArrayValueDetail({ value }: { value: unknown }) {
  if (!Array.isArray(value) || value.length === 0) return null;

  const details = filterNulls(
    value.map((item) => {
      const parsedItem = displayPrimitiveSchema.safeParse(item);
      if (parsedItem.success) return parsedItem.data;

      const itemRecord = parseRecordOrNull(item);
      if (!itemRecord) return null;

      return Object.values(itemRecord)
        .map((itemValue) => displayPrimitiveSchema.safeParse(itemValue))
        .map((parsedItemValue) => {
          return parsedItemValue.success ? parsedItemValue.data : null;
        })
        .find((itemValue) => itemValue);
    }),
  );

  return details.length > 0 ? details.join(", ") : null;
}

function getRecordValueDetail({ value }: { value: unknown }) {
  const parsedValue = displayPrimitiveSchema.safeParse(value);
  if (parsedValue.success) return parsedValue.data;

  return getArrayValueDetail({ value });
}

export function getObjectDetail({
  properties,
  value,
}: {
  properties: readonly string[];
  value: Record<string, unknown> | null;
}) {
  if (!value || Object.keys(value).length === 0) return null;

  for (const property of properties) {
    const propertyDetail = getRecordValueDetail({ value: value[property] });
    if (propertyDetail) return propertyDetail;
  }

  return null;
}

export function getToolInputString({
  properties,
  toolPart,
}: {
  properties: readonly string[];
  toolPart: OpencodeToolPart;
}) {
  return getObjectDetail({ properties, value: toolPart.state.input });
}
